import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { FInter, FlexBox, Skeleton, useIsMobile } from '@brg/ui';
import { useRouteMatch } from "react-router-dom";
import { ICategoryLight } from '../../common';
import { useTranslation } from 'react-i18next';
import { ProjectSearchBarSorting } from './components/projectSearchBarSorting';
import { ProjectSearchBartDisplay } from './components/projectSearchBartDisplay';
import { Tag } from '@brg/api';
import styled, { useTheme } from 'styled-components';
import { SortingTypeIndex } from '../../constants/sortingType';
import { DisplayType } from '../../constants/displayType';
import { bTag, useModal } from '../../../core';
import { BTag } from '../../../core/components';
import { UInput } from '../inputs';
import { SearchBarWrapper, SearchBarHolder, Checkboxes, CheckboxesDropdownContainer, CheckboxesDropdownItem } from "./style";
import { MagicBoxRow } from '../tagAdminProjectsSearchBar/tagAdminProjectsSearchBar';
import { AppTester } from "../../../supporting";
import useQueryState from "../../hooks/useQueryState";
import TagsTextBar from '../TagsTextBar/TagsTextBar';
import { SortingTypeIndexPeople } from "../../pages/context/contextViewClear";
import { BTagsGridNew } from "../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import SettingsIcon from 'shared/assets/32/SettingsIcon';
import { ClickOutsideComponent } from '../CommentsController/components/ClickOutside';
import useAppTester from 'shared/MobileMenu/hooks/useAppTester';

const ZEROING_PROJECTS_PAGE = 1;

export interface ProjectsSearchBarLayoutProps {
  isLoading?: boolean;
  isCategoriesHidden?: boolean;
  isTagsHidden?: boolean;
  tagsText?: ReactNode;
  categoriesText?: ReactNode;
  categorySelect?: ReactNode;
  tagsSelect?: ReactNode;
  methods?: ReactNode;
  hideRightBar?: boolean;
  hideLeftBar?: boolean;
  leftBarContent?: ReactNode;
  column?: boolean;
  rightSectionColumn?: boolean;
  isMobile?: boolean;
}

export const ProjectsSearchBarFlex = styled(FlexBox)`
  .tags-line {
    width: 100%;
  }
`;

export const ProjectsSearchBarLayout: FC<ProjectsSearchBarLayoutProps> = (props) => {
  const {
    isLoading,
    tagsSelect,
    tagsText,
    isTagsHidden,
    hideLeftBar,
    leftBarContent,
    rightSectionColumn
  } = props;

  return (
    <SearchBarWrapper flex="1" justify="space-between" align={"center"} inLine={!!leftBarContent} className={'search-bar-wrapper'}>
      { (!hideLeftBar || leftBarContent) && <FlexBox align="flex-end" gx={24} width={'100%'}>
        <SearchBarHolder
          direction={rightSectionColumn ? 'column' : 'row'}
          width={375}
          style={{
            maxWidth: '375px',
            opacity: isTagsHidden ? 0 : 1,
            pointerEvents: isTagsHidden ? 'none' : 'auto',
            display: hideLeftBar && !leftBarContent ? 'none' : 'flex'
          }}
          gy={5}
        >
          {leftBarContent}
          {isLoading ? <Skeleton width={44} height={20} /> : !leftBarContent && tagsText}
          {isLoading ? <Skeleton width={375} height={48} /> : !leftBarContent && tagsSelect}
        </SearchBarHolder>
      </FlexBox>}
      <FlexBox gx={24}>
        {isLoading ? null : props.methods}
      </FlexBox>
    </SearchBarWrapper>
  );
};

export function createAllCategories(name: string) {
  return {
    identifier: 'all',
    title: 'all',
    name,
  };
}

export interface ProjectsSearchBarProps {
  isCategoriesHidden?: boolean;
  isTagHidden?: boolean;
  isLoading?: boolean;
  categories?: ICategoryLight[];
  categoryValue?: ICategoryLight;
  tags?: Tag[];
  tagValue?: Tag[];
  display?: DisplayType;
  sorting?: SortingTypeIndex | SortingTypeIndexPeople;
  sortingMethods?: SortingTypeIndex[] | SortingTypeIndexPeople[];
  onDisplayChange?: () => void;
  onCategoryChange?: (category: ICategoryLight) => void;
  onExtraFilterChange?: (filter: any) => void;
  extraFilter?: any;
  onTagsChange?: (tags: Tag[]) => void;
  onSortingChange?: (sorting: any) => void;
  hideRightBar?: boolean;
  hideLeftBar?: boolean;
  leftBarContent?: ReactNode;
  hideRightButtons?: boolean;
  isTagPage?: boolean;
  isTagOwnerOrModer?: boolean;
  projectCount?: number;
  column?: boolean;
  isPeoplePage?: boolean;
  isDarkTheme?: boolean;
  isChannel?: boolean;
  onToggleUnrated?: () => void;
  isCompetitions?: boolean;
  openCheckboxes?: boolean;
  setOpenCheckboxes?: (value: boolean) => void;
  showcaseVisible?: boolean;
  isShowTeachersCheckbox?: boolean;
  showCheckboxes?: boolean;
  productCards?: boolean;
  onProductCardsChange?: (value: boolean) => void;
  mobileSortingBar?: boolean;
}

export const ProjectsSearchBar: FC<ProjectsSearchBarProps> = (props) => {
  const {
    isLoading,
    onCategoryChange,
    onTagsChange,
    hideRightBar,
    hideLeftBar,
    leftBarContent,
    hideRightButtons,
    isPeoplePage,
    isTagPage,
    isTagOwnerOrModer,
    isDarkTheme,
    extraFilter,
    onExtraFilterChange,
    isChannel,
    isCompetitions,
    openCheckboxes,
    setOpenCheckboxes,
    showcaseVisible,
    isShowTeachersCheckbox,
    showCheckboxes,
    mobileSortingBar,
    productCards: productCardsProp,
    onProductCardsChange,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const modal = useModal();
  const isMobile = useIsMobile();

  const {globalTypes: extraFilterGlobalTypes} = extraFilter || {}
  
  const [{ teachersOnly, unrated, studentOnly, showcase, productCards: productCardsQuery }, setQueryParams] = useQueryState();
  const productCards = onProductCardsChange ? !!productCardsProp : productCardsQuery;
  const allCategories: ICategoryLight[] = useMemo(
    () => [
      createAllCategories(t('all')),
      ...(props.categories ?? [])
    ],
    [t, props.categories],
  );

  const {
    isHSEDesign,
    isGreenAward,
    isGeoMain,
    isAcademy,
    isFr,
    isJhonataNet: isJhonata
  } = useAppTester();

  const isGeoStyle = isGeoMain || isAcademy
  const isModerTagPage = useRouteMatch('/tag');

  const isAKI = AppTester.isAKI();

  const isAnyCheckboxChecked = useMemo(() => {
    return (
      isChannel ? Boolean(extraFilter) : extraFilterGlobalTypes?.includes('employee') ||
      !!showcase ||
      !!productCards
    )
  }, [isChannel, extraFilter, showcase, productCards, extraFilterGlobalTypes])

  const isTeacherCheckBoxChecked = useMemo(() => {
    return isChannel ? Boolean(extraFilter) : extraFilterGlobalTypes?.includes('employee')
  }, [extraFilter, isChannel])

  const shouldDisplayProducts = !isFr
  const shouldDisplayAuthors = !!onExtraFilterChange && ((isHSEDesign && !isModerTagPage)  || isShowTeachersCheckbox)
  const shouldDisplayShowcase = showcaseVisible

  const shouldDisplayCheckboxes = shouldDisplayAuthors || shouldDisplayShowcase || shouldDisplayProducts

  const circleIconColor = useMemo(() => {
        if (isHSEDesign) return '#146DEE' 
        if (isGreenAward || isGeoStyle) return '#89B644'
        if (isJhonata) return '#f1db63'
        return '#ff0000'
  }, [isHSEDesign, isGreenAward, isGeoStyle, isJhonata])

  if (!shouldDisplayCheckboxes) {
    return null;
  }

  return (
    <>
      <ProjectsSearchBarLayout
        isCategoriesHidden={props.isCategoriesHidden}
        isTagsHidden={props.isTagHidden}
        isLoading={isLoading}
        hideRightBar={hideRightBar}
        hideLeftBar={hideLeftBar}
        leftBarContent={leftBarContent}
        categoriesText={<FInter.SmallText color={theme.tags.textColor}>{t('category')}</FInter.SmallText>}
        categorySelect={
          <UInput.Select
            value={props.categoryValue}
            onChange={onCategoryChange}
            placeholder={t('placeholders.category')}
            options={allCategories}
          />
        }
        tagsSelect={
          <ProjectsSearchBarFlex gx={24}>
            {!isTagPage && (
              <TagsTextBar
                onClick={() =>
                  modal.show('tags', {
                    mode: 'link',
                  })
                }
              />
            )}

          </ProjectsSearchBarFlex>
        }
        methods={
          <FlexBox gx={mobileSortingBar ? 0 : 24}
            direction={isTagPage && !isTagOwnerOrModer ? 'row' : (isTagOwnerOrModer && isTagPage) ? 'column' : 'row'}
            justify='flex-end' style={{ placeItems: hideLeftBar ? "flex-end" : "normal" }}>

            {showCheckboxes &&
            <ClickOutsideComponent onClose={() => setOpenCheckboxes && setOpenCheckboxes(false)}>
              <Checkboxes circleColor={circleIconColor} isAnyChecked={isAnyCheckboxChecked} >
                <div onClick={() => {
                  setOpenCheckboxes && setOpenCheckboxes(!openCheckboxes)}
                }>
                  {!isAKI && (
                  <div className='sortbar-checkbox-icon-wrapper'>
                    <SettingsIcon />
                  </div>
                  )}
                </div>
                {openCheckboxes && <CheckboxesDropdownContainer>
                  {shouldDisplayAuthors && ( 
                                <CheckboxesDropdownItem style={{ placeItems: 'center' }}>
                                  <MagicBoxRow
                                    label={isChannel ? t('filter.only_author') : t('filter.teacher_projects')}
                                    checked={isTeacherCheckBoxChecked}
                                    style={{ margin: isMobile ? '0' : '0 0 0 0', color: theme.sortByColor }}
                                    onClick={() => {

                                      if (isChannel) {
                                        onExtraFilterChange?.(!extraFilter)
                                        return;
                                      }

                                      onExtraFilterChange?.({
                                        globalTypes: !extraFilter?.globalTypes?.includes('employee') ? ['employee'] : [],

                                      })
                                      setQueryParams({
                                        teachersOnly: teachersOnly === 'true' ? '' : 'true',
                                        page: ZEROING_PROJECTS_PAGE
                                      })
                                    }}
                                  />
                                </CheckboxesDropdownItem>
                              )}
                              {
                                shouldDisplayShowcase && <CheckboxesDropdownItem style={{ placeItems: 'center' }}>
                                  <MagicBoxRow
                                    label={t("filter.showcase_title")}
                                    checked={showcase}
                                    style={{ color: theme.sortByColor }}
                                    onClick={() => {
                                      setQueryParams({ showcase: !showcase })
                                    }}
                                  />
                                </CheckboxesDropdownItem>
                              }
                              {
                                shouldDisplayProducts && <CheckboxesDropdownItem style={{ placeItems: 'center' }}>
                                  <MagicBoxRow
                                    label={t("products")}
                                    checked={productCards}
                                    style={{ margin: isMobile ? '0' : '0 0 0 0', color: theme.sortByColor }}
                                    onClick={() => {
                                      if (onProductCardsChange) {
                                        onProductCardsChange(!productCards);
                                        return;
                                      }
                                      setQueryParams({productCards: !productCards})
                                    }}
                                  />
                                </CheckboxesDropdownItem>
                              }
                </CheckboxesDropdownContainer>}
              </Checkboxes>
            </ClickOutsideComponent>
            }

            {!hideRightBar && (
              <ProjectSearchBarSorting
                selectedSorting={props.sorting ?? 1}
                onSortingChange={props.onSortingChange}
                sortingMethods={props.sortingMethods}
                isPeoplePage={isPeoplePage}
                isDarkTheme={isDarkTheme}
                isCompetitions={isCompetitions}
                mobileSortingBar={mobileSortingBar}
              />
            )}

            {!hideRightButtons && (
              <ProjectSearchBartDisplay
                display={props.display ?? DisplayType.map}
                onDisplayChange={props.onDisplayChange}
                hideRightBar={props.hideRightBar}
              />
            )}
          </FlexBox>
        }
        column={props?.column || true}
        rightSectionColumn={isModerTagPage ? true : false}
      />
      {!!props.tagValue?.length && (
        <BTagsGridNew style={{ marginTop: 10 }}>
          {props.tagValue?.map((selectedTagItem) => (
            <BTag
              tag={selectedTagItem}
              removable={true}
              key={selectedTagItem.tagId}
              onClick={() => {
                const vi = props.tagValue?.filter((tag) => tag.tagId !== selectedTagItem.tagId);
                onTagsChange?.(vi ?? []);
              }}
            >
              {bTag.title(selectedTagItem)}
            </BTag>
          ))}
        </BTagsGridNew>
      )}
    </>
  );
};
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { FInter, FlexBox, Skeleton, useIsMobile } from '@brg/ui';
import { useRouteMatch } from "react-router-dom";
import { ICategoryLight } from '../../common';
import { useTranslation } from 'react-i18next';
import { ProjectSearchBarSorting } from './components/projectSearchBarSorting';
import { ProjectSearchBartDisplay } from './components/projectSearchBartDisplay';
import { Tag } from '@brg/api';
import styled, { useTheme } from 'styled-components';
import { SortingTypeIndex } from '../../constants/sortingType';
import { DisplayType } from '../../constants/displayType';
import { bTag, useModal } from '../../../core';
import { BTag } from '../../../core/components';
import { UInput } from '../inputs';
import { SearchBarWrapper, SearchBarHolder, Checkboxes, CheckboxesDropdownContainer, CheckboxesDropdownItem } from "./style";
import { MagicBoxRow } from '../tagAdminProjectsSearchBar/tagAdminProjectsSearchBar';
import { AppTester } from "../../../supporting";
import useQueryState from "../../hooks/useQueryState";
import TagsTextBar from '../TagsTextBar/TagsTextBar';
import { SortingTypeIndexPeople } from "../../pages/context/contextViewClear";
import { BTagsGridNew } from "../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import SettingsIcon from 'shared/assets/32/SettingsIcon';
import { ClickOutsideComponent } from '../CommentsController/components/ClickOutside';
import useAppTester from 'shared/MobileMenu/hooks/useAppTester';

const ZEROING_PROJECTS_PAGE = 1;

export interface ProjectsSearchBarLayoutProps {
  isLoading?: boolean;
  isCategoriesHidden?: boolean;
  isTagsHidden?: boolean;
  tagsText?: ReactNode;
  categoriesText?: ReactNode;
  categorySelect?: ReactNode;
  tagsSelect?: ReactNode;
  methods?: ReactNode;
  hideRightBar?: boolean;
  hideLeftBar?: boolean;
  leftBarContent?: ReactNode;
  column?: boolean;
  rightSectionColumn?: boolean;
  isMobile?: boolean;
}

export const ProjectsSearchBarFlex = styled(FlexBox)`
  .tags-line {
    width: 100%;
  }
`;

export const ProjectsSearchBarLayout: FC<ProjectsSearchBarLayoutProps> = (props) => {
  const {
    isLoading,
    tagsSelect,
    tagsText,
    isTagsHidden,
    hideLeftBar,
    leftBarContent,
    rightSectionColumn
  } = props;

  return (
    <SearchBarWrapper flex="1" justify="space-between" align={"center"} inLine={!!leftBarContent} className={'search-bar-wrapper'}>
      { (!hideLeftBar || leftBarContent) && <FlexBox align="flex-end" gx={24} width={'100%'}>
        <SearchBarHolder
          direction={rightSectionColumn ? 'column' : 'row'}
          width={375}
          style={{
            maxWidth: '375px',
            opacity: isTagsHidden ? 0 : 1,
            pointerEvents: isTagsHidden ? 'none' : 'auto',
            display: hideLeftBar && !leftBarContent ? 'none' : 'flex'
          }}
          gy={5}
        >
          {leftBarContent}
          {isLoading ? <Skeleton width={44} height={20} /> : !leftBarContent && tagsText}
          {isLoading ? <Skeleton width={375} height={48} /> : !leftBarContent && tagsSelect}
        </SearchBarHolder>
      </FlexBox>}
      <FlexBox gx={24}>
        {isLoading ? null : props.methods}
      </FlexBox>
    </SearchBarWrapper>
  );
};

export function createAllCategories(name: string) {
  return {
    identifier: 'all',
    title: 'all',
    name,
  };
}

export interface ProjectsSearchBarProps {
  isCategoriesHidden?: boolean;
  isTagHidden?: boolean;
  isLoading?: boolean;
  categories?: ICategoryLight[];
  categoryValue?: ICategoryLight;
  tags?: Tag[];
  tagValue?: Tag[];
  display?: DisplayType;
  sorting?: SortingTypeIndex | SortingTypeIndexPeople;
  sortingMethods?: SortingTypeIndex[] | SortingTypeIndexPeople[];
  onDisplayChange?: () => void;
  onCategoryChange?: (category: ICategoryLight) => void;
  onExtraFilterChange?: (filter: any) => void;
  extraFilter?: any;
  onTagsChange?: (tags: Tag[]) => void;
  onSortingChange?: (sorting: any) => void;
  hideRightBar?: boolean;
  hideLeftBar?: boolean;
  leftBarContent?: ReactNode;
  hideRightButtons?: boolean;
  isTagPage?: boolean;
  isTagOwnerOrModer?: boolean;
  projectCount?: number;
  column?: boolean;
  isPeoplePage?: boolean;
  isDarkTheme?: boolean;
  isChannel?: boolean;
  onToggleUnrated?: () => void;
  isCompetitions?: boolean;
  openCheckboxes?: boolean;
  setOpenCheckboxes?: (value: boolean) => void;
  showcaseVisible?: boolean;
  isShowTeachersCheckbox?: boolean;
  showCheckboxes?: boolean;
  productCards?: boolean;
  onProductCardsChange?: (value: boolean) => void;
  mobileSortingBar?: boolean;
}

export const ProjectsSearchBar: FC<ProjectsSearchBarProps> = (props) => {
  const {
    isLoading,
    onCategoryChange,
    onTagsChange,
    hideRightBar,
    hideLeftBar,
    leftBarContent,
    hideRightButtons,
    isPeoplePage,
    isTagPage,
    isTagOwnerOrModer,
    isDarkTheme,
    extraFilter,
    onExtraFilterChange,
    isChannel,
    isCompetitions,
    openCheckboxes,
    setOpenCheckboxes,
    showcaseVisible,
    isShowTeachersCheckbox,
    showCheckboxes,
    mobileSortingBar,
    productCards: productCardsProp,
    onProductCardsChange,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const modal = useModal();
  const isMobile = useIsMobile();

  const {globalTypes: extraFilterGlobalTypes} = extraFilter || {}
  
  const [{ teachersOnly, unrated, studentOnly, showcase, productCards: productCardsQuery }, setQueryParams] = useQueryState();
  const productCards = onProductCardsChange ? !!productCardsProp : productCardsQuery;
  const allCategories: ICategoryLight[] = useMemo(
    () => [
      createAllCategories(t('all')),
      ...(props.categories ?? [])
    ],
    [t, props.categories],
  );

  const {
    isHSEDesign,
    isGreenAward,
    isGeoMain,
    isAcademy,
    isFr,
    isJhonataNet: isJhonata
  } = useAppTester();

  const isGeoStyle = isGeoMain || isAcademy
  const isModerTagPage = useRouteMatch('/tag');

  const isAKI = AppTester.isAKI();

  const isAnyCheckboxChecked = useMemo(() => {
    return (
      isChannel ? Boolean(extraFilter) : extraFilterGlobalTypes?.includes('employee') ||
      !!showcase ||
      !!productCards
    )
  }, [isChannel, extraFilter, showcase, productCards, extraFilterGlobalTypes])

  const isTeacherCheckBoxChecked = useMemo(() => {
    return isChannel ? Boolean(extraFilter) : extraFilterGlobalTypes?.includes('employee')
  }, [extraFilter, isChannel])

  const shouldDisplayProducts = !isFr
  const shouldDisplayAuthors = !!onExtraFilterChange && ((isHSEDesign && !isModerTagPage)  || isShowTeachersCheckbox)
  const shouldDisplayShowcase = showcaseVisible

  const shouldDisplayCheckboxes = shouldDisplayAuthors || shouldDisplayShowcase || shouldDisplayProducts

  const circleIconColor = useMemo(() => {
        if (isHSEDesign) return '#146DEE' 
        if (isGreenAward || isGeoStyle) return '#89B644'
        if (isJhonata) return '#f1db63'
        return '#ff0000'
  }, [isHSEDesign, isGreenAward, isGeoStyle, isJhonata])

  if (!shouldDisplayCheckboxes) {
    return null;
  }

  return (
    <>
      <ProjectsSearchBarLayout
        isCategoriesHidden={props.isCategoriesHidden}
        isTagsHidden={props.isTagHidden}
        isLoading={isLoading}
        hideRightBar={hideRightBar}
        hideLeftBar={hideLeftBar}
        leftBarContent={leftBarContent}
        categoriesText={<FInter.SmallText color={theme.tags.textColor}>{t('category')}</FInter.SmallText>}
        categorySelect={
          <UInput.Select
            value={props.categoryValue}
            onChange={onCategoryChange}
            placeholder={t('placeholders.category')}
            options={allCategories}
          />
        }
        tagsSelect={
          <ProjectsSearchBarFlex gx={24}>
            {!isTagPage && (
              <TagsTextBar
                onClick={() =>
                  modal.show('tags', {
                    mode: 'link',
                  })
                }
              />
            )}

          </ProjectsSearchBarFlex>
        }
        methods={
          <FlexBox gx={mobileSortingBar ? 0 : 24}
            direction={isTagPage && !isTagOwnerOrModer ? 'row' : (isTagOwnerOrModer && isTagPage) ? 'column' : 'row'}
            justify='flex-end' style={{ placeItems: hideLeftBar ? "flex-end" : "normal" }}>

            {showCheckboxes &&
            <ClickOutsideComponent onClose={() => setOpenCheckboxes && setOpenCheckboxes(false)}>
              <Checkboxes circleColor={circleIconColor} isAnyChecked={isAnyCheckboxChecked} >
                <div onClick={() => {
                  setOpenCheckboxes && setOpenCheckboxes(!openCheckboxes)}
                }>
                  {!isAKI && (
                  <div className='sortbar-checkbox-icon-wrapper'>
                    <SettingsIcon />
                  </div>
                  )}
                </div>
                {openCheckboxes && <CheckboxesDropdownContainer>
                  {shouldDisplayAuthors && ( 
                                <CheckboxesDropdownItem style={{ placeItems: 'center' }}>
                                  <MagicBoxRow
                                    label={isChannel ? t('filter.only_author') : t('filter.teacher_projects')}
                                    checked={isTeacherCheckBoxChecked}
                                    style={{ margin: isMobile ? '0' : '0 0 0 0', color: theme.sortByColor }}
                                    onClick={() => {

                                      if (isChannel) {
                                        onExtraFilterChange?.(!extraFilter)
                                        return;
                                      }

                                      onExtraFilterChange?.({
                                        globalTypes: !extraFilter?.globalTypes?.includes('employee') ? ['employee'] : [],

                                      })
                                      setQueryParams({
                                        teachersOnly: teachersOnly === 'true' ? '' : 'true',
                                        page: ZEROING_PROJECTS_PAGE
                                      })
                                    }}
                                  />
                                </CheckboxesDropdownItem>
                              )}
                              {
                                shouldDisplayShowcase && <CheckboxesDropdownItem style={{ placeItems: 'center' }}>
                                  <MagicBoxRow
                                    label={t("filter.showcase_title")}
                                    checked={showcase}
                                    style={{ color: theme.sortByColor }}
                                    onClick={() => {
                                      setQueryParams({ showcase: !showcase })
                                    }}
                                  />
                                </CheckboxesDropdownItem>
                              }
                              {
                                shouldDisplayProducts && <CheckboxesDropdownItem style={{ placeItems: 'center' }}>
                                  <MagicBoxRow
                                    label={t("products")}
                                    checked={productCards}
                                    style={{ margin: isMobile ? '0' : '0 0 0 0', color: theme.sortByColor }}
                                    onClick={() => {
                                      if (onProductCardsChange) {
                                        onProductCardsChange(!productCards);
                                        return;
                                      }
                                      setQueryParams({productCards: !productCards})
                                    }}
                                  />
                                </CheckboxesDropdownItem>
                              }
                </CheckboxesDropdownContainer>}
              </Checkboxes>
            </ClickOutsideComponent>
            }

            {!hideRightBar && (
              <ProjectSearchBarSorting
                selectedSorting={props.sorting ?? 1}
                onSortingChange={props.onSortingChange}
                sortingMethods={props.sortingMethods}
                isPeoplePage={isPeoplePage}
                isDarkTheme={isDarkTheme}
                isCompetitions={isCompetitions}
                mobileSortingBar={mobileSortingBar}
              />
            )}

            {!hideRightButtons && (
              <ProjectSearchBartDisplay
                display={props.display ?? DisplayType.map}
                onDisplayChange={props.onDisplayChange}
                hideRightBar={props.hideRightBar}
              />
            )}
          </FlexBox>
        }
        column={props?.column || true}
        rightSectionColumn={isModerTagPage ? true : false}
      />
      {!!props.tagValue?.length && (
        <BTagsGridNew style={{ marginTop: 10 }}>
          {props.tagValue?.map((selectedTagItem) => (
            <BTag
              tag={selectedTagItem}
              removable={true}
              key={selectedTagItem.tagId}
              onClick={() => {
                const vi = props.tagValue?.filter((tag) => tag.tagId !== selectedTagItem.tagId);
                onTagsChange?.(vi ?? []);
              }}
            >
              {bTag.title(selectedTagItem)}
            </BTag>
          ))}
        </BTagsGridNew>
      )}
    </>
  );
};
